/**
 * scheduleResolve.ts
 * Suggests a one-click fix for a hard-conflicting MachineBooking (UX — M7).
 *
 * Strategy:
 *   1. Nearest free slot on the SAME machine (earliest start in the window where
 *      the booking's duration fits without overlapping another booking).
 *   2. Failing that, a CAPABLE machine that is free over the booking's current
 *      range.
 * Returns a placement patch + a human label, or null if nothing was found.
 *
 * RM-23 (Run Mode audit hardening): the scan is now run-mode aware, matching
 * the backend placement oracle (setBookingStart / findFirstFreeStartContinuous):
 *   - candidate STARTS are working days only (weekend/holiday starts were
 *     proposed and persisted silently — a placement the engine's own search
 *     would never produce, and one that dates continuous setup staffing on an
 *     invisible non-working day);
 *   - when the moved booking is CONTINUOUS, occupied neighbour ranges are
 *     teardown-PADDED (a range ending on a non-working day extends to the next
 *     working day — the rig is physically occupied until teardown, Amendment
 *     #4), so the suggestion can't drop the booking onto a neighbour's
 *     teardown working day. The capable-machine fallback applies the same pad.
 */

import type { MachineBookingCalendar } from '../types/schedule';
import type { BookingPlacementPatch } from '../hooks/useScheduleData';
import { isNonWorkingDay } from './weekUtils';
import { isContinuousRun, nextWorkingDayIso } from './runModeRendering';

export interface ResolveSuggestion {
  patch: BookingPlacementPatch;
  label: string;
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function durationDays(startIso: string, endIso: string): number {
  const a = new Date(`${startIso}T00:00:00`).getTime();
  const b = new Date(`${endIso}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}

function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

/** Occupied [start, end] of a neighbour, teardown-padded when the MOVED booking
 *  is continuous (FE mirror of padOccupiedRangesForTeardown). */
function occupiedEnd(
  neighbour: MachineBookingCalendar,
  padTeardown: boolean,
  holidays: readonly string[],
): string {
  if (!padTeardown) return neighbour.EndDate;
  return nextWorkingDayIso(neighbour.EndDate, holidays);
}

export function suggestResolution(
  booking: MachineBookingCalendar,
  allBookings: MachineBookingCalendar[],
  capableMachineKeys: Set<string> | null,
  machineCode: (key: string) => string,
  days: string[],
  /** RM-23: stat-holiday ISO dates — candidate starts skip them. Optional so
   *  older callers degrade to weekend-only awareness. */
  holidays: readonly string[] = [],
): ResolveSuggestion | null {
  const dur = durationDays(booking.StartDate, booking.EndDate);
  const continuous = isContinuousRun(booking.RunMode);
  const holidaySet = holidays.length ? new Set(holidays) : undefined;

  // 1. nearest free slot on the same machine, scanning the visible window.
  const sameMachine = allBookings.filter(
    (b) => b.MachineKey === booking.MachineKey && b.MachineBookingKey !== booking.MachineBookingKey,
  );
  for (const startISO of days) {
    if (startISO === booking.StartDate) continue;
    // RM-23: never propose a weekend/holiday start (both modes — the backend
    // oracle anchors starts to working days for attended AND continuous).
    if (isNonWorkingDay(startISO, holidaySet)) continue;
    const endISO = addDays(startISO, dur);
    const clash = sameMachine.some((o) =>
      overlaps(startISO, endISO, o.StartDate, occupiedEnd(o, continuous, holidays)),
    );
    if (!clash) {
      return { patch: { StartDate: startISO, EndDate: endISO }, label: `Moved to ${startISO}` };
    }
  }

  // 2. a capable machine that is free over the booking's current range.
  if (capableMachineKeys) {
    for (const mk of capableMachineKeys) {
      if (mk === booking.MachineKey) continue;
      const onMachine = allBookings.filter(
        (b) => b.MachineKey === mk && b.MachineBookingKey !== booking.MachineBookingKey,
      );
      const clash = onMachine.some((o) =>
        overlaps(booking.StartDate, booking.EndDate, o.StartDate, occupiedEnd(o, continuous, holidays)),
      );
      if (!clash) {
        return { patch: { MachineKey: mk }, label: `Moved to ${machineCode(mk)}` };
      }
    }
  }

  return null;
}
